import { createContext, useContext, ReactNode, useState, useEffect, useMemo } from "react";

import { DispatchType, FormInputType } from "@bridgesplit/react";
import {
    isStrategyDurationSame,
    OrderbookQuote,
    useOrderbookQuotes,
    useStrategyDurations
} from "@bridgesplit/abf-react";
import { doNothing } from "@bridgesplit/utils";
import { StrategyDuration, TokenListMetadata, formatDurationWithTypeShorthand } from "@bridgesplit/abf-sdk";
import { useSearchParams } from "react-router-dom";
import { AppParams } from "app/utils";

import { getDefaultCollateral, useMarketCollateral } from "../util";
import { useMarketContext } from "./MarketContext";
import { ALL_COLLATERAL_VALUE } from "./constants";

type QuoteContextState = {
    strategyDuration: StrategyDuration | undefined;
    setStrategyDuration: FormInputType<StrategyDuration | undefined>;
    collateralMints: string[];
    collateralMintsForQueries: string[] | undefined; // for fetching using collateral mints
    setCollateralMints: DispatchType<string[]>;
    strategyDurations: StrategyDuration[] | undefined;
    orderbookQuotes: OrderbookQuote[] | undefined;
    collateralToken: TokenListMetadata | undefined;
};

const QuotesContext = createContext<QuoteContextState | null>(null);

export function useQuotesContext() {
    const context = useContext(QuotesContext);
    if (!context) {
        throw new Error("useQuotesContext must be used within a QuotesProvider");
    }
    return context;
}

export function HiddenQuotesProvider({ children }: { children: ReactNode }) {
    return (
        <QuotesContext.Provider
            value={{
                strategyDuration: undefined,
                setStrategyDuration: doNothing,
                collateralMints: [],
                setCollateralMints: doNothing,
                strategyDurations: undefined,
                orderbookQuotes: undefined,
                collateralToken: undefined,
                collateralMintsForQueries: undefined
            }}
        >
            {children}
        </QuotesContext.Provider>
    );
}

export interface QuoteContextProps {
    defaultCollateralMint?: string;
}

// Helper to detect "ALL" by value, not by reference
const isAllArray = (arr?: string[]) => Array.isArray(arr) && arr.length === 0;

export function QuotesProvider({ children, value }: { children: ReactNode; value?: QuoteContextProps }) {
    const { principalMint } = useMarketContext();

    const [strategyDuration, setStrategyDuration] = useState<StrategyDuration>();
    const [collateralMints, setCollateralMints] = useState<string[]>(ALL_COLLATERAL_VALUE);
    const [collateralInitialized, setCollateralInitialized] = useState(false);

    const [search] = useSearchParams();

    const allTokens = useMarketCollateral();
    const allMints = allTokens?.map((t) => t.mint);

    // Use robust "ALL" detection
    const isAllCollateral = isAllArray(collateralMints);

    const collateralToken =
        !isAllCollateral && collateralMints?.length === 1
            ? allTokens?.find((c) => c.mint === collateralMints[0])
            : undefined;

    const collateralMintsForQueries = useMemo(() => {
        if (!collateralInitialized) return undefined;
        return isAllCollateral ? allMints : collateralMints;
    }, [allMints, collateralInitialized, collateralMints, isAllCollateral]);

    const { strategyDurations } = useStrategyDurations();

    const { data: orderbookQuotes, isFetching } = useOrderbookQuotes({
        strategyDuration,
        principalMint,
        collateralMints: collateralMintsForQueries
    });

    const termShortHand = search.get(AppParams.Term);

    // NOTE: getDefaultCollateral({ returnAll: true }) may return "ALL" (string) — normalize below.
    const rawSpecifiedMint =
        search.get(AppParams.CollateralMint) ?? getDefaultCollateral({ principalMint, returnAll: true });
    const hasDefaultCollateralProp = value
        ? Object.prototype.hasOwnProperty.call(value, "defaultCollateralMint")
        : false;

    // normalize helper: if given "ALL", set to ALL_COLLATERAL_VALUE; else set [mint]
    const setCollateralNormalized = (mintLike?: string) => {
        if (!mintLike) return;
        if (mintLike === "ALL") {
            setCollateralMints(ALL_COLLATERAL_VALUE);
        } else {
            setCollateralMints([mintLike]);
        }
    };

    // initial collateral from props / URL / default
    useEffect(() => {
        if (collateralInitialized) return;

        if (value?.defaultCollateralMint) {
            setCollateralNormalized(value.defaultCollateralMint);
            setCollateralInitialized(true);
            return;
        }

        if (rawSpecifiedMint) {
            setCollateralNormalized(rawSpecifiedMint);
            setCollateralInitialized(true);
            return;
        }

        if (!hasDefaultCollateralProp) {
            setCollateralMints(ALL_COLLATERAL_VALUE);
            setCollateralInitialized(true);
        }
    }, [collateralInitialized, hasDefaultCollateralProp, rawSpecifiedMint, value?.defaultCollateralMint]);

    // Switch to ALL if user selects the principal mint as collateral
    useEffect(() => {
        if (!!principalMint && !isAllCollateral && collateralMints?.includes(principalMint)) {
            setCollateralMints(ALL_COLLATERAL_VALUE);
        }
    }, [collateralMints, principalMint, isAllCollateral]);

    // React to defaultCollateralMint prop changes (normalize)
    useEffect(() => {
        const mint = value?.defaultCollateralMint;
        if (!mint) return;
        let updated = false;
        setCollateralMints((prev) => {
            // if already normalized ALL and incoming is ALL, keep
            if (mint === "ALL" && isAllArray(prev)) return prev;
            // if single mint and equal, keep
            if (prev.length === 1 && prev[0] === mint) return prev;
            updated = true;
            return mint === "ALL" ? ALL_COLLATERAL_VALUE : [mint];
        });
        if (updated || !collateralInitialized) {
            setCollateralInitialized(true);
        }
    }, [collateralInitialized, value?.defaultCollateralMint]);

    // Ensure a valid strategyDuration whenever durations or term param changes
    useEffect(() => {
        if (!strategyDurations?.length) return;

        if (!strategyDuration || !strategyDurations.some((p) => isStrategyDurationSame(strategyDuration, p))) {
            const def =
                strategyDurations.find(
                    (p) => formatDurationWithTypeShorthand(p.duration, p.durationType) === termShortHand
                ) ?? strategyDurations[0];
            setStrategyDuration(def);
        }
    }, [strategyDuration, strategyDurations, termShortHand]);

    return (
        <QuotesContext.Provider
            value={{
                strategyDuration,
                setStrategyDuration,
                collateralMints,
                collateralMintsForQueries,
                setCollateralMints,
                strategyDurations,
                orderbookQuotes: isFetching ? undefined : orderbookQuotes,
                collateralToken
            }}
        >
            {children}
        </QuotesContext.Provider>
    );
}
